import React, { useCallback, useEffect, useState, useRef } from "react";
import { Checkbox } from "@progress/kendo-react-inputs";
import { filterBy, CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { uploadIcon, trashIcon } from "@progress/kendo-svg-icons";

import { useInjection } from "@presentation/components/dependencyInjection";
import { UploadSettingsDialog } from "@presentation/components/UploadSettingsDialog";
import { ListSoftwareSettingsService, DeleteSoftwareSettingsService } from "@application/useCases";
import { SoftwareSettingsMetadata, SettingsTypeEnumSchema } from "@domain/schemas/BI";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { markItemsAsLastUsed, sortItemsByLastUsed } from "@presentation/common/utils/lastUsedOrdering";

interface GenericMultiSelectSettingsFormProps {
  settingsType: SettingsTypeEnumSchema;
  placeholder?: string;
  uploadButtonTitle?: string;
}

export const GenericMultiSelectSettingsForm: React.FC<GenericMultiSelectSettingsFormProps> = ({ settingsType, placeholder = "Click to select files...", uploadButtonTitle = "Upload new file" }) => {
  const settingsLastUsedStorageKey = `job-setup-settings-last-used-${settingsType}`;
  const normalizePath = (path?: string | null) => (path ?? "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
  const toFullPath = (location?: string | null, name?: string | null) => normalizePath([location, name].filter(Boolean).join("/"));
  const getSettingsStorageKey = useCallback((item: SoftwareSettingsMetadata) => toFullPath(item.location, item.name) || item.id, []);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsList, setSettingsList] = useState<SoftwareSettingsMetadata[]>([]);
  const [selectedSettings, setSelectedSettings] = useState<SoftwareSettingsMetadata[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSize, setDropdownSize] = useState({ width: 600, height: 400 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const softwareVersion = useJobSetupStore((state) => state.CreateJobState.softwareVersion);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const multiSelectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { settingsDirectDIA, reportSettings, goSettings, modificationRepository, conditionSetup, setInJobStore } = useJobSetupStore((state) => ({
    settingsDirectDIA: state.settingsDirectDIA,
    reportSettings: state.reportSettings,
    goSettings: state.goSettings,
    modificationRepository: state.modificationRepository,
    conditionSetup: state.conditionSetup,
      setInJobStore: (() => {
        switch (settingsType) {
        case "Experiment":
          return state.setDirectDIASettings;
        case "Report":
          return state.setReportSettings;
        case "GO":
          return state.setGOSettings;
        case "Modification":
          return state.setModificationRepository;
        case "ConditionSetup":
          return state.setConditionSetup;
        default:
          throw new Error(`Unsupported settings type: ${settingsType}`);
      }
    })(),
  }));

  // Load persisted settings on component mount
  useEffect(() => {
    const getPersistedSettings = () => {
      switch (settingsType) {
        case "Experiment":
          return settingsDirectDIA;
        case "Report":
          return reportSettings;
        case "GO":
          return goSettings;
        case "Modification":
          return modificationRepository;
        case "ConditionSetup":
          return conditionSetup;
        default:
          return [];
      }
    };

    const persistedSettings = getPersistedSettings();
    if (persistedSettings) {
      const settingsArray = Array.isArray(persistedSettings) ? persistedSettings : [persistedSettings];
      const validSettings = settingsArray.filter((s) => s && s.id); // only keep valid objects
      if (validSettings.length > 0) {
        setSelectedSettings(validSettings);
      }
    }
  }, [settingsType, settingsDirectDIA, reportSettings, goSettings, modificationRepository, conditionSetup]);

  // Handle mouse events for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setDropdownPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
      if (isResizing) {
        const rect = dropdownRef.current?.getBoundingClientRect();
        if (rect) {
          setDropdownSize({
            width: Math.max(300, e.clientX - rect.left),
            height: Math.max(200, e.clientY - rect.top),
          });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  // Position dropdown below the multiselect
  useEffect(() => {
    if (dropdownOpen && multiSelectRef.current) {
      const rect = multiSelectRef.current.getBoundingClientRect();

      // Calculate initial position
      let x = rect.left;
      let y = rect.bottom + 5;

      // Constrain to window boundaries
      x = Math.max(10, Math.min(x, window.innerWidth - dropdownSize.width - 10));
      y = Math.max(10, Math.min(y, window.innerHeight - dropdownSize.height - 10));

      setDropdownPosition({ x, y });

      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [dropdownOpen, dropdownSize.height, dropdownSize.width]);

  const filteredData = filter ? filterBy(settingsList, filter) : settingsList;
  const getVersionMetadata = useCallback((version?: string | null) => {
    const normalizedVersion = (version ?? "").trim().replace(/^(?:SN|SM)/i, "");
    const match = normalizedVersion.match(/^v?(\d+)\.\d+\.\d+(?:[.-].*)?$/i);
    return match ? { major: match[1] } : null;
  }, []);
  const getSettingsMajor = useCallback((item: SoftwareSettingsMetadata) => {
    const major = item.detectedMajorVersion;
    return major === null || major === undefined ? null : String(major);
  }, []);
  const getSettingsCompatibility = useCallback((item: SoftwareSettingsMetadata) => {
    const selectedVersion = getVersionMetadata(softwareVersion);
    const major = getSettingsMajor(item);
    const isLegacy = !item.detectedSoftwareVersion && !item.detectedMajorVersion;
    if (!selectedVersion || isLegacy || !major) return { compatible: true, reason: isLegacy ? "Legacy settings" : "Unversioned settings" };
    const compatible = major === selectedVersion.major;
    return {
      compatible,
      reason: compatible ? `Compatible with Spectronaut ${selectedVersion.major}` : `Incompatible: detected Spectronaut ${major}, selected Spectronaut ${selectedVersion.major}`,
    };
  }, [getSettingsMajor, getVersionMetadata, softwareVersion]);
  const sortSettings = useCallback((items: SoftwareSettingsMetadata[]) =>
    sortItemsByLastUsed(
      items,
      settingsLastUsedStorageKey,
      getSettingsStorageKey,
      (item) => item.name,
    ), [getSettingsStorageKey, settingsLastUsedStorageKey]);

  useEffect(() => {
    if (selectedSettings.length === 0) return;

    const compatibleSelection = selectedSettings.filter((setting) => getSettingsCompatibility(setting).compatible);
    if (compatibleSelection.length !== selectedSettings.length) {
      setSelectedSettings(compatibleSelection);
      setInJobStore(compatibleSelection);
    }
  }, [getSettingsCompatibility, selectedSettings, setInJobStore, softwareVersion]);

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    const rect = dropdownRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  const openSettingsDialog = () => setShowSettingsDialog(true);
  const closeSettingsDialog = () => setShowSettingsDialog(false);

  const listSettingsService = useInjection(ListSoftwareSettingsService);
  const deleteSettingsService = useInjection(DeleteSoftwareSettingsService);

  useEffect(() => {
    async function fetchSettingsList() {
      try {
        const listResponse = await listSettingsService.getSettingsListAsync(settingsType);
        if (listResponse.list) {
          setSettingsList(sortSettings(listResponse.list));
        }
      } catch (error) {
        console.error(`Error fetching settings list for ${settingsType}:`, error);
      }
    }
    fetchSettingsList();
  }, [listSettingsService, settingsType, sortSettings]);

  const afterSettingsUploadSuccess = (uploadedSettings: SoftwareSettingsMetadata) => {
    markItemsAsLastUsed(settingsLastUsedStorageKey, [getSettingsStorageKey(uploadedSettings)]);
    setSettingsList(sortSettings([uploadedSettings, ...settingsList]));
    setSelectedSettings([uploadedSettings]);
    setInJobStore([uploadedSettings]);
    setShowSettingsDialog(false);
  };

  const matchesSelectedSetting = (candidate: SoftwareSettingsMetadata, selected: SoftwareSettingsMetadata) => {
    if (candidate.id === selected.id) return true;
    return toFullPath(candidate.location, candidate.name) === toFullPath(selected.location, selected.name);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} ref={multiSelectRef}>
        <div style={{ width: "70%", position: "relative", cursor: "pointer" }} onClick={() => setDropdownOpen(!dropdownOpen)}>
          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: "3px",
              padding: "8px",
              minHeight: "32px",
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "4px",
            }}
          >
            {selectedSettings.length > 0 ? (
              selectedSettings.map((setting) => (
                <span
                  key={setting.id}
                  style={{
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #2196f3",
                    borderRadius: "3px",
                    padding: "2px 8px",
                    fontSize: "12px",
                    color: "#1976d2",
                  }}
                >
                  {setting.name}
                </span>
              ))
            ) : (
              <span style={{ color: "#999" }}>{placeholder}</span>
            )}
          </div>
        </div>

        <Button
          svgIcon={uploadIcon}
          onClick={openSettingsDialog}
          style={{
            width: "27%",
            borderRadius: "3px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          title={uploadButtonTitle}
        >
          {uploadButtonTitle}
        </Button>
      </div>

      {dropdownOpen && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "transparent",
              zIndex: 999,
            }}
            onClick={() => setDropdownOpen(false)}
          />
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.x,
              top: dropdownPosition.y,
              width: dropdownSize.width,
              height: dropdownSize.height,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "3px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #e0e0e0",
                cursor: "move",
                userSelect: "none",
                borderRadius: "4px 4px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onMouseDown={handleDragStart}
            >
              <span style={{ fontWeight: "500", fontSize: "14px" }}>Select Settings</span>
              <button
                onClick={() => setDropdownOpen(false)}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            {/* Filter Input */}
            <div style={{ padding: "12px" }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by filename..."
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "3px",
                  fontSize: "14px",
                }}
                onChange={(e) => {
                  const filterValue = e.target.value.trim();
                  if (filterValue) {
                    const words = filterValue.split(" ");
                    const wordFilters = words.map((word: string) => ({
                      logic: "or",
                      filters: [
                        { field: "name", operator: "contains", value: word, ignoreCase: true },
                        { field: "createdBy", operator: "contains", value: word, ignoreCase: true },
                      ],
                    }));
                    setFilter({ logic: "and", filters: wordFilters } as CompositeFilterDescriptor);
                  } else {
                    setFilter(undefined);
                  }
                }}
              />
            </div>

            {/* Items List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                borderBottom: "1px solid #e0e0e0",
              }}
              >
              {filteredData.map((item) => {
                const isSelected = selectedSettings.some((selected) => matchesSelectedSetting(item, selected));
                const compatibility = getSettingsCompatibility(item);
                const isCompatible = compatibility.compatible;
                // const isLegacy = !item.detectedSoftwareVersion && !item.detectedMajorVersion;
                return (
                  <div
                    key={item.id}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                      opacity: isCompatible ? 1 : 0.6,
                    }}
                    onClick={() => {
                      if (!isCompatible) {
                        return;
                      }

                      const currentSelected = [...selectedSettings];
                      const index = currentSelected.findIndex((selected) => matchesSelectedSetting(item, selected));

                      if (index > -1) {
                        currentSelected.splice(index, 1);
                      } else {
                        const singleSelectionTypes = ["Experiment", "ConditionSetup", "Modification"];
                        if (singleSelectionTypes.includes(settingsType)) {
                          currentSelected.splice(0, currentSelected.length, item);
                          setDropdownOpen(false);
                        } else {
                          currentSelected.push(item);
                        }
                      }

                      markItemsAsLastUsed(
                        settingsLastUsedStorageKey,
                        currentSelected.map(getSettingsStorageKey),
                      );
                      setSelectedSettings(currentSelected);
                      setInJobStore(currentSelected);
                      setSettingsList(sortSettings(settingsList));
                    }}
                  >
                    <Checkbox checked={isSelected} style={{ marginRight: 8 }} readOnly={true} />
                    <div style={{ flex: 2, overflow: "hidden", marginRight: 8 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                      {/* <div style={{ fontSize: "11px", color: isLegacy ? "#b26a00" : "#666" }}>
                        {isLegacy
                          ? "Legacy settings without detected version metadata"
                          : `Detected: ${item.detectedSoftwareVersion ?? item.softwareVersion} (${compatibility.reason})`}
                      </div> */}
                    </div>
                    <span style={{ flex: 1, fontSize: "0.9em", color: "#666" }}>By: {item.createdBy}</span>
                    <Button
                      svgIcon={trashIcon}
                      fillMode="flat"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm(`Are you sure you want to delete ${item.name}?`)) {
                          try {
                            await deleteSettingsService.executeAsync(item.name, settingsType);

                            // Remove from selected if present
                            const updatedSelected = selectedSettings.filter((s) => s.id !== item.id);
                            if (updatedSelected.length !== selectedSettings.length) {
                              setSelectedSettings(updatedSelected);
                              setInJobStore(updatedSelected);
                            }

                            // Update list
                            const updatedList = settingsList.filter((s) => s.id !== item.id);
                            setSettingsList(updatedList);
                          } catch (error) {
                            console.error("Failed to delete settings file", error);
                            alert("Failed to delete file.");
                          }
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Resize Handle */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "20px",
                height: "20px",
                cursor: "se-resize",
                backgroundColor: "#f0f0f0",
                borderLeft: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                borderRadius: "0 0 4px 0",
              }}
              onMouseDown={handleResizeStart}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "2px",
                  right: "2px",
                  width: "0",
                  height: "0",
                  borderLeft: "8px solid transparent",
                  borderBottom: "8px solid #999",
                }}
              />
            </div>
          </div>
        </>
      )}

      {showSettingsDialog && <UploadSettingsDialog onUploadSuccess={afterSettingsUploadSuccess} settingsType={settingsType} onCancel={closeSettingsDialog} />}
    </div>
  );
};
